import { Injectable } from '@angular/core';
import { GeolocationPlugin } from '@capacitor/geolocation/dist/esm/definitions';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Geolocation,
  PermissionStatus,
  Position,
} from '@capacitor/geolocation';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { App, AppState } from '@capacitor/app';
import { ProminentLocationDisclosureModalComponent } from '../components/prominent-location-disclosure-modal/prominent-location-disclosure-modal.component';
import * as Sentry from '@sentry/angular';
import BackgroundGeolocation, {
  ProviderChangeEvent,
  Subscription as BgSubscription,
} from '@transistorsoft/capacitor-background-geolocation';
import { TranslateService } from '@ngx-translate/core';
import { ensureError } from '../../shared/helpers/ensure-error.helper';
import { translatePermissionRationale } from '../../shared/helpers/translate-permission-rationale.helper';
import { LoggerService } from 'src/app/services/logger.service';
import { PreferencesService } from 'src/app/services/preferences.service';

export enum GeolocationPermission {
  Unknown = 'unknown', // for scenarios like fresh installation
  LocationServiceDisabled = 'location-service-disabled', // this one is created to indicate that the device location services is disabled
  Prompt = 'prompt',
  PromptWithRationale = 'prompt-with-rationale',
  Granted = 'granted',
  Denied = 'denied',
  Failure = 'failure',
}

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private geolocation: GeolocationPlugin;

  private _permission$: BehaviorSubject<GeolocationPermission> =
    new BehaviorSubject<GeolocationPermission>(GeolocationPermission.Unknown);

  private positionOptions: PositionOptions = {
    enableHighAccuracy: true,
    maximumAge: 3000,
  };

  // iOS fires no event when the user keeps "While Using", so we wait this long
  // for an "Always" grant before treating it as declined and showing the modal.
  private readonly IOS_ALWAYS_UPGRADE_TIMEOUT_MS: number = 10000;

  // Marks that the user declined the iOS "Always" upgrade. iOS won't re-present
  // the prompt once declined, so later attempts skip the wait and go straight
  // to the force-modal's Settings guidance.
  //
  // Persisted via PreferencesService (native Preferences):
  // this mirrors the OS-level permission state, which is
  // device-scoped and unaffected by logging out
  private readonly IOS_ALWAYS_DECLINED_STORAGE_KEY: string =
    'ios_always_declined';

  // Prominent background-location disclosure: the in-app disclosure must be
  // shown and affirmatively accepted on every native platform before we ever
  // request background ("Always") location.
  //
  // Persisted via PreferencesService (native Preferences):
  // this mirrors the OS-level permission state, which is
  // device-scoped and unaffected by logging out
  private readonly BACKGROUND_LOCATION_DISCLOSURE_ACCEPTED_KEY: string =
    'background_location_disclosure_accepted';

  // When the OS prompt is dismissed (app returns to foreground) we re-check
  // after this beat, leaving room for a grant's onProviderChange to win the
  // race before we treat the dismissal as a decline.
  private readonly IOS_PROMPT_DISMISS_RECHECK_MS: number = 400;

  private logger = new LoggerService(GeolocationService.name);

  constructor(
    private translateService: TranslateService,
    private preferencesService: PreferencesService,
    private modalController: ModalController,
  ) {
    this.geolocation = Geolocation;
  }

  // Stream of the latest known foreground location permission.
  get permission(): Observable<GeolocationPermission> {
    return this._permission$;
  }

  public isPermissionGranted(): boolean {
    return this._permission$.value === GeolocationPermission.Granted;
  }

  public async currentNativePosition(): Promise<Position> {
    return await Geolocation.getCurrentPosition();
  }

  public async getPermissions(): Promise<GeolocationPermission> {
    let permission: GeolocationPermission;

    if (!Capacitor.isNativePlatform()) {
      permission = await this.getPermissionsWeb();
    }

    if (Capacitor.isNativePlatform()) {
      permission = await this.getPermissionsNative();
    }

    this._permission$.next(permission!);

    return permission!;
  }

  async getPermissionsWeb(): Promise<GeolocationPermission> {
    const result: globalThis.PermissionStatus =
      await navigator.permissions.query({ name: 'geolocation' });

    return result.state as GeolocationPermission;
  }

  private async getPermissionsNative(): Promise<GeolocationPermission> {
    let permission: GeolocationPermission;

    try {
      const result: PermissionStatus =
        await this.geolocation.checkPermissions();

      permission = result.location as GeolocationPermission;
    } catch (error) {
      const safeError: Error = ensureError(error);

      Sentry.captureException(safeError);

      permission = GeolocationPermission.LocationServiceDisabled;
    }

    return permission;
  }

  private requestPermissionsWeb(): Promise<GeolocationPermission> {
    return new Promise((resolve): void => {
      navigator.geolocation.getCurrentPosition(
        (): void => {
          const permission: GeolocationPermission.Granted =
            GeolocationPermission.Granted;
          this._permission$.next(permission);
          resolve(permission);
        },
        (positionError: GeolocationPositionError): void => {
          const code: number = positionError.code;
          let permission: GeolocationPermission = GeolocationPermission.Failure;

          if (code === GeolocationPositionError.PERMISSION_DENIED) {
            permission = GeolocationPermission.Denied;
          }

          if (code === GeolocationPositionError.POSITION_UNAVAILABLE) {
            permission = GeolocationPermission.LocationServiceDisabled;
          }

          this._permission$.next(permission);
          resolve(permission);
        },
        this.positionOptions,
      );
    });
  }

  private async requestPermissionNative(): Promise<GeolocationPermission> {
    return await this.geolocation
      .requestPermissions({
        permissions: ['location'],
      })
      .then((permission: PermissionStatus): GeolocationPermission => {
        return permission.location as GeolocationPermission;
      })
      .catch((error): GeolocationPermission => {
        let permission: GeolocationPermission = GeolocationPermission.Failure;

        if (error.message === 'Location services are not enabled') {
          permission = GeolocationPermission.LocationServiceDisabled;
        }

        return permission;
      });
  }

  public async requestPermissions(): Promise<boolean> {
    let permission: GeolocationPermission;

    if (!Capacitor.isNativePlatform()) {
      permission = await this.requestPermissionsWeb();
    }

    if (Capacitor.isNativePlatform()) {
      permission = await this.requestPermissionNative();
    }

    this._permission$.next(permission!);

    return this.isPermissionGranted();
  }

  public async hasAlwaysAuthorization(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    const state: ProviderChangeEvent =
      await BackgroundGeolocation.getProviderState();

    return this.isStateAlways(state);
  }

  public async requireAlwaysAuthorization(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    const state: ProviderChangeEvent =
      await BackgroundGeolocation.getProviderState();

    if (this.isStateAlways(state)) {
      void this.persistIosAlwaysDeclined(false);

      return true;
    }

    // Prominent background-location disclosure: present the in-app disclosure
    // and require affirmative consent before requesting the background
    // ("Always") location permission from the OS.
    if (!(await this.ensureBackgroundLocationDisclosure())) {
      return false;
    }

    await BackgroundGeolocation.setConfig({
      backgroundPermissionRationale: translatePermissionRationale(
        this.translateService,
      ),
    });

    if (Capacitor.getPlatform() === 'ios') {
      return this.requireAlwaysAuthorizationIos(state);
    }

    return this.requireAlwaysAuthorizationAndroid(state);
  }

  /**
   * Non-blocking read of the persisted background-location consent flag.
   * It's used on app launch to decide if the geolocation plugin may be
   * re-attached immediately (e.g. when iOS relaunches the app in the
   * background after the process was killed).
   */
  public async hasAcceptedBackgroundLocationDisclosure(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    try {
      return (
        (await this.preferencesService.get(
          this.BACKGROUND_LOCATION_DISCLOSURE_ACCEPTED_KEY,
        )) === 'true'
      );
    } catch (error) {
      this.logger.warn({
        method: 'hasAcceptedBackgroundLocationDisclosure',
        identifier: 'movacarpro_geolocation_disclosure_read_failed',
        message: 'failed to read background location disclosure flag',
        value: error,
      });

      return false;
    }
  }

  public async ensureBackgroundLocationDisclosure(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    if (await this.hasAcceptedBackgroundLocationDisclosure()) {
      return true;
    }

    const modal: HTMLIonModalElement = await this.modalController.create({
      component: ProminentLocationDisclosureModalComponent,
      backdropDismiss: false,
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<{ accepted: boolean }>();
    const accepted: boolean = data?.accepted === true;

    if (accepted) {
      try {
        await this.preferencesService.set(
          this.BACKGROUND_LOCATION_DISCLOSURE_ACCEPTED_KEY,
          'true',
        );
      } catch (error) {
        this.logger.warn({
          method: 'ensureBackgroundLocationDisclosure',
          identifier: 'movacarpro_geolocation_disclosure_persist_failed',
          message: 'failed to persist background location disclosure flag',
          value: error,
        });
      }
    }

    return accepted;
  }

  private async requireAlwaysAuthorizationAndroid(
    state: ProviderChangeEvent,
  ): Promise<boolean> {
    if (
      state.status === BackgroundGeolocation.AUTHORIZATION_STATUS_NOT_DETERMINED
    ) {
      try {
        await BackgroundGeolocation.requestPermission();
      } catch (error) {
        this.logger.warn({
          method: 'requireAlwaysAuthorizationAndroid',
          identifier: 'movacarpro_geolocation_request_permission_declined',
          message: 'requestPermission declined',
          value: error,
        });
      }
    }

    try {
      await BackgroundGeolocation.start();
      await BackgroundGeolocation.getCurrentPosition({});
    } catch (error) {
      this.logger.warn({
        method: 'requireAlwaysAuthorizationAndroid',
        identifier: 'movacarpro_geolocation_prime_always_failed',
        message: 'priming background authorization failed',
        value: error,
      });
    } finally {
      await BackgroundGeolocation.stop();
    }

    return this.isStateAlways(await BackgroundGeolocation.getProviderState());
  }

  private async requireAlwaysAuthorizationIos(
    state: ProviderChangeEvent,
  ): Promise<boolean> {
    if (
      state.status === BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED ||
      state.status === BackgroundGeolocation.AUTHORIZATION_STATUS_RESTRICTED
    ) {
      return false;
    }

    // Already declined once: iOS won't re-show the prompt, so skip the wait.
    if (
      (await this.preferencesService.get(
        this.IOS_ALWAYS_DECLINED_STORAGE_KEY,
      )) === 'true'
    ) {
      return false;
    }

    return this.primeAndAwaitIosAlways();
  }

  private primeAndAwaitIosAlways(): Promise<boolean> {
    return new Promise<boolean>((resolve): void => {
      let settled: boolean = false;
      let primingComplete: boolean = false;
      let wentInactive: boolean = false;
      let subscription: BgSubscription | undefined;
      let appStateListener: PluginListenerHandle | undefined;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const finish = (granted: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;

        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }

        subscription?.remove();
        void appStateListener?.remove();
        void BackgroundGeolocation.stop();
        void this.persistIosAlwaysDeclined(!granted);
        resolve(granted);
      };

      subscription = BackgroundGeolocation.onProviderChange(
        (event: ProviderChangeEvent): void => {
          if (this.isStateAlways(event)) {
            finish(true);

            return;
          }

          if (
            primingComplete &&
            (event.status ===
              BackgroundGeolocation.AUTHORIZATION_STATUS_WHEN_IN_USE ||
              event.status ===
                BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED)
          ) {
            finish(false);
          }
        },
      );

      // Presenting the OS prompt sends the app inactive; dismissing it (either
      // choice) brings it back. That return-to-foreground is our signal the
      // prompt was answered, so we can decide immediately instead of waiting
      // out the timeout. A grant is caught by onProviderChange above; the brief
      // re-check delay lets that win before a dismissal is read as a decline.
      void App.addListener('appStateChange', (state: AppState): void => {
        if (!state.isActive) {
          wentInactive = true;

          return;
        }

        if (!wentInactive) {
          return;
        }

        setTimeout((): void => {
          void BackgroundGeolocation.getProviderState().then(
            (current: ProviderChangeEvent): void =>
              finish(this.isStateAlways(current)),
          );
        }, this.IOS_PROMPT_DISMISS_RECHECK_MS);
      }).then((handle: PluginListenerHandle): void => {
        appStateListener = handle;

        if (settled) {
          void handle.remove();
        }
      });

      timeoutId = setTimeout((): void => {
        // Backstop in case the prompt never fires app lifecycle events on this
        // iOS version: a quiet window means the upgrade was declined. The
        // force-modal keeps listening and continues if Always is granted later
        // (via its own prompt or Settings).
        finish(false);
      }, this.IOS_ALWAYS_UPGRADE_TIMEOUT_MS);

      void (async (): Promise<void> => {
        try {
          await BackgroundGeolocation.start();
          await BackgroundGeolocation.getCurrentPosition({});
        } catch (error) {
          this.logger.warn({
            method: 'primeAndAwaitIosAlways',
            identifier: 'movacarpro_geolocation_prime_always_failed',
            message: 'priming background authorization failed',
            value: error,
          });
        } finally {
          primingComplete = true;
        }
      })();
    });
  }

  private async persistIosAlwaysDeclined(declined: boolean): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      if (!declined) {
        await this.preferencesService.remove(
          this.IOS_ALWAYS_DECLINED_STORAGE_KEY,
        );

        return;
      }

      await this.preferencesService.set(
        this.IOS_ALWAYS_DECLINED_STORAGE_KEY,
        'true',
      );
    } catch (error) {
      this.logger.warn({
        method: 'persistIosAlwaysDeclined',
        identifier: 'movacarpro_geolocation_persist_declined_failed',
        message: 'failed to persist iOS Always-declined flag',
        value: error,
      });
    }
  }

  private isStateAlways(state: ProviderChangeEvent): boolean {
    return (
      state.enabled &&
      state.status === BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS
    );
  }
}
